/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

export interface ILanguageUrlConfiguration {
    languageName: string;
    languageUrl: {
        destinationUrl: string;
    };
    isMainSiteLanguage?: boolean;
}

/**
 * Builds language-selector URLs that retain the route below the active language root.
 *
 * Examples:
 * /vision-mission-values -> /cn/vision-mission-values
 * /cn/vision-mission-values -> /vision-mission-values
 * /eshop/our-souvenirs -> /eshopcn/our-souvenirs
 *
 * Query strings and hash fragments are retained because they may contain valid page state.
 * If the current URL is outside the configured active-language root, the configured
 * target-language homepage is returned as a safe fallback.
 */
export const buildLocalizedLanguageUrls = (languages: ILanguageUrlConfiguration[], currentUrl: string): string[] => {
    if (!languages.length || !currentUrl) {
        return languages.map(language => language.languageUrl.destinationUrl);
    }

    const currentLanguage = languages.find(language => language.isMainSiteLanguage) || languages[0];

    try {
        const current = new URL(currentUrl);
        const currentLanguageRoot = new URL(currentLanguage.languageUrl.destinationUrl, current.origin);
        const relativePath = getRelativePath(current.pathname, currentLanguageRoot.pathname);

        return languages.map(language => {
            const targetLanguageRoot = new URL(language.languageUrl.destinationUrl, current.origin);

            if (relativePath === undefined) {
                return targetLanguageRoot.toString();
            }

            targetLanguageRoot.pathname = joinPaths(targetLanguageRoot.pathname, relativePath);
            targetLanguageRoot.search = current.search;
            targetLanguageRoot.hash = current.hash;

            return targetLanguageRoot.toString();
        });
    } catch (_error) {
        return languages.map(language => language.languageUrl.destinationUrl);
    }
};

const getRelativePath = (currentPath: string, languageRootPath: string): string | undefined => {
    const normalizedCurrentPath = normalizePath(currentPath);
    const normalizedLanguageRootPath = normalizePath(languageRootPath);

    if (normalizedCurrentPath === normalizedLanguageRootPath) {
        return '';
    }

    if (normalizedLanguageRootPath === '/') {
        return normalizedCurrentPath;
    }

    const languageRootWithSeparator = `${normalizedLanguageRootPath}/`;

    if (normalizedCurrentPath.startsWith(languageRootWithSeparator)) {
        return normalizedCurrentPath.slice(normalizedLanguageRootPath.length);
    }

    return undefined;
};

const joinPaths = (languageRootPath: string, relativePath: string): string => {
    const normalizedLanguageRootPath = normalizePath(languageRootPath);

    if (!relativePath) {
        return normalizedLanguageRootPath;
    }

    if (normalizedLanguageRootPath === '/') {
        return normalizePath(relativePath);
    }

    return `${normalizedLanguageRootPath}${relativePath.startsWith('/') ? relativePath : `/${relativePath}`}`;
};

const normalizePath = (path: string): string => {
    if (!path || path === '/') {
        return '/';
    }

    const pathWithLeadingSeparator = path.startsWith('/') ? path : `/${path}`;
    return pathWithLeadingSeparator.replace(/\/+$/, '');
};
